import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { ExpenseAttachment } from '@/store/reducers/payout';
import useLanguage from '@/components/common/hooks/useLanguage';
import {
  download,
  formatSize,
  isImage,
  openInNewTab,
  useAttachmentUrl,
} from '@/utils/attachments';

interface Props {
  attachments: ExpenseAttachment[];
  startAt: number;
  onClose: () => void;
}

/**
 * Full-screen look at the proof on a line, one file at a time. Portalled to the
 * body for the reason BottomSheet is: it opens from inside a sheet whose
 * transform would otherwise make it the containing block and clip it. Only the
 * opacity animates -- nothing here is centred with a transform.
 */
const AttachmentViewer: React.FC<Props> = ({ attachments, startAt, onClose }) => {
  const [{ payout, common }] = useLanguage();
  const [index, setIndex] = useState(Math.min(startAt, attachments.length - 1));
  const [isIn, setIsIn] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const touchX = useRef<number>();

  const attachment = attachments[index];
  const { url, failed } = useAttachmentUrl(attachment);
  const hasMany = attachments.length > 1;

  const go = (step: number) => {
    setImageFailed(false);
    setIndex((i) => (i + step + attachments.length) % attachments.length);
  };

  // Two frames, as in BottomSheet: with one the fade is coalesced away.
  useEffect(() => {
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setIsIn(true));
    });

    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasMany) go(-1);
      if (e.key === 'ArrowRight' && hasMany) go(1);
    };
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [hasMany, onClose]);

  if (!attachment) return null;

  const showImage = isImage(attachment.mimeType) && !imageFailed;

  return createPortal(
    <Overlay
      isIn={isIn}
      // Clicks must not reach a sheet this was opened from: React bubbles through
      // the portal to its owner, and a sheet's backdrop closes on click.
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === undefined || !hasMany) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        touchX.current = undefined;
      }}
    >
      <Top>
        <Name title={attachment.fileName}>{attachment.fileName}</Name>
        {hasMany && <Counter>{`${index + 1} / ${attachments.length}`}</Counter>}
        <IconButton aria-label={common.close} onClick={onClose}>×</IconButton>
      </Top>

      <Stage onClick={onClose}>
        {url && showImage && (
          <Picture
            src={url}
            alt={attachment.fileName}
            onClick={(e) => e.stopPropagation()}
            onError={() => setImageFailed(true)}
          />
        )}
        {(failed || (url && !showImage)) && (
          <Card onClick={(e) => e.stopPropagation()}>
            <Kind>{attachment.mimeType === 'application/pdf' ? 'PDF' : 'FILE'}</Kind>
            <CardName>{attachment.fileName}</CardName>
            <CardMeta>
              {failed ? payout.attachment_failed : formatSize(attachment.size)}
            </CardMeta>
            {url && !isImage(attachment.mimeType) && (
              <CardButton onClick={() => openInNewTab(url)}>{payout.open_file}</CardButton>
            )}
            {url && isImage(attachment.mimeType) && (
              <CardMeta>{payout.no_preview}</CardMeta>
            )}
          </Card>
        )}
      </Stage>

      <Bottom>
        <NavButton disabled={!hasMany} onClick={() => go(-1)} aria-label="previous">‹</NavButton>
        <DownloadButton
          disabled={!url}
          onClick={() => url && download(url, attachment.fileName)}
        >
          {payout.download}
        </DownloadButton>
        <NavButton disabled={!hasMany} onClick={() => go(1)} aria-label="next">›</NavButton>
      </Bottom>
    </Overlay>,
    document.body,
  );
};

const Overlay = styled.div<{ isIn: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  background: #0f1114;
  opacity: ${({ isIn }) => (isIn ? 1 : 0)};
  transition: opacity 180ms ease;
  color: #ffffff;
  user-select: none;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 8px 8px 16px;
  min-height: 56px;
`;

const Name = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Counter = styled.div`
  font-size: 13px;
  color: #b8bcc0;
  margin: 0 8px;
  white-space: nowrap;
`;

const IconButton = styled.button`
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  color: #ffffff;
  font-size: 30px;
  line-height: 44px;
  cursor: pointer;
`;

const Stage = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
`;

const Picture = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 320px;
  padding: 28px 20px;
  border-radius: 12px;
  background: #ffffff;
  color: #21272e;
  text-align: center;
`;

const Kind = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #d9534f;
  letter-spacing: 1px;
`;

const CardName = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-top: 10px;
  word-break: break-word;
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: #909599;
  margin-top: 6px;
`;

const CardButton = styled.button`
  margin-top: 18px;
  height: 44px;
  padding: 0 28px;
  border: none;
  border-radius: 6px;
  background: #fb7e14;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;

const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 20px;
`;

const NavButton = styled.button`
  width: 52px;
  height: 44px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font-size: 26px;
  line-height: 44px;
  cursor: pointer;

  :disabled {
    visibility: hidden;
  }
`;

const DownloadButton = styled.button`
  height: 44px;
  padding: 0 24px;
  border: solid 1px rgba(255, 255, 255, 0.35);
  border-radius: 6px;
  background: transparent;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  :disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export default AttachmentViewer;
